<template>
  <div class="app-shell home">
    <header class="home__top">
      <img class="home__logo" src="/assets/logo.jpg" alt="" />

      <span v-if="isLive" class="tb-pill tb-pill--live">
        <span class="tb-pill__dot"></span>
        Live now
      </span>
      <vac v-else :end-time="scheduleMoment.toDate()">
        <template v-slot:process="{ timeObj }">
          <div class="countdown">
            <div v-for="part in countdownParts(timeObj)" :key="part.label" class="countdown__part">
              <span class="countdown__value">{{ part.value }}</span>
              <span class="countdown__label">{{ part.label }}</span>
            </div>
          </div>
        </template>
        <template v-slot:finish>
          <span class="tb-pill">Starting soon</span>
        </template>
      </vac>
    </header>

    <div class="tb-card when">
      <div class="when__cell">
        <span class="when__label">Draw date</span>
        <strong class="when__value">{{ scheduleMoment.format("MMM Do YY") }}</strong>
      </div>
      <div class="when__cell">
        <span class="when__label">Draw time</span>
        <strong class="when__value">{{ scheduleMoment.format("hh:mm A") }}</strong>
      </div>
    </div>

    <div class="home__actions">
      <Links />
      <button v-if="info.show_agent" type="button" class="tb-btn tb-btn--accent" @click="agentsModal = true">AGENT LIST</button>
    </div>

    <b-field class="home__search">
      <b-input v-model.trim="query" type="search" icon="magnify" placeholder="Find your ticket by number or name" aria-label="Find your ticket"></b-input>
    </b-field>

    <h2 class="tb-section-title">
      Tickets
      <span class="tb-pill">{{ results.length }}</span>
    </h2>

    <div class="tb-stack">
      <TicketCard
        v-for="item in visibleTickets"
        :key="item.number"
        :card="item"
        :class="{ 'is-sheet-end': Number(item.number) % 6 === 0 }"
      />

      <p v-if="!results.length" class="tb-empty">
        {{ query ? `No ticket matches “${query}”.` : "Tickets for this game are not out yet." }}
      </p>

      <button v-if="hiddenCount" type="button" class="tb-btn" @click="showMore">Show more tickets ({{ hiddenCount }} left)</button>
    </div>

    <b-pagination
      v-if="info.show_paginate && results.length"
      class="home__pagination"
      :total="results.length"
      :range-before="2"
      :range-after="2"
      :current.sync="current"
      :per-page="perPage"
      :rounded="true"
      simple
      order="is-centered"
      icon-prev="chevron-left"
      icon-next="chevron-right"
      aria-next-label="Next page"
      aria-previous-label="Previous page"
      aria-page-label="Page"
      aria-current-label="Current page"
    ></b-pagination>

    <footer class="home__footer">
      <strong class="text-funky"><span>&#169;</span> {{ info.name }}</strong>
      <small>All Right Reserved. {{ year }}</small>
    </footer>

    <b-modal :active.sync="agentsModal" has-modal-card trap-focus :destroy-on-hide="false" aria-role="dialog" aria-modal>
      <AgentList></AgentList>
    </b-modal>
  </div>
</template>

<script>
  import moment from "moment";
  import { mapState } from "vuex";
  import "firebase/firestore";
  import firebase from "firebase/app";
  import Links from "../components/Links.vue";
  import AgentList from "../components/AgentList.vue";
  import TicketCard from "../components/TicketCard.vue";

  // How many tickets to render at a time when pagination is switched off.
  // Drawing all 600 at once locks up a phone for seconds.
  const CHUNK = 24;

  export default {
    name: "Home",
    components: { Links, AgentList, TicketCard },
    data() {
      return {
        agentsModal: false,
        perPage: 50,
        current: 1,
        shown: CHUNK,
        query: ""
      };
    },
    computed: {
      ...mapState(["cards", "info", "currentGame"]),
      isLive() {
        return this.currentGame.status === "live";
      },
      year() {
        return new Date().getFullYear();
      },
      // The schedule arrives as a Firestore timestamp, but the store seeds it
      // with a plain Date before the first fetch resolves.
      scheduleMoment() {
        const schedule = this.currentGame && this.currentGame.schedule;
        if (schedule && typeof schedule.seconds === "number") {
          return moment(schedule.seconds * 1000);
        }
        return moment(schedule || undefined);
      },
      results() {
        const query = this.query.toLowerCase();
        if (!query) return this.cards;
        return this.cards.filter((card) => card.number.includes(query) || (card.name && card.name.toLowerCase().includes(query)));
      },
      visibleTickets() {
        if (this.info.show_paginate) {
          return this.results.slice(this.perPage * (this.current - 1), this.perPage * this.current);
        }
        return this.results.slice(0, this.shown);
      },
      hiddenCount() {
        if (this.info.show_paginate) return 0;
        return Math.max(0, this.results.length - this.shown);
      }
    },
    watch: {
      // Without this a search run from page 3 lands on an empty page.
      query() {
        this.current = 1;
        this.shown = CHUNK;
      }
    },
    mounted() {
      if (this.info.alert) {
        this.alertTimer = setTimeout(() => this.$buefy.dialog.alert(this.info.alert), 4000);
      }

      // Jump straight to the board the moment a game goes live.
      this.unsubscribe = firebase
        .firestore()
        .collection("games")
        .where("status", "==", "live")
        .onSnapshot((next) => {
          if (next.docChanges().length) {
            this.$store.commit("SET_GAME_STATUS", "live");
            this.$router.push("/live");
          }
        });

      console.log("%cDeveloped By justraman.dev", "font-size: 40px; color: black");
      console.log("contact: me@justraman.dev");
    },
    beforeDestroy() {
      clearTimeout(this.alertTimer);
      if (this.unsubscribe) this.unsubscribe();
    },
    methods: {
      countdownParts(timeObj) {
        return [
          { label: "D", value: timeObj.d },
          { label: "H", value: timeObj.h },
          { label: "M", value: timeObj.m },
          { label: "S", value: timeObj.s }
        ];
      },
      showMore() {
        this.shown += CHUNK;
      }
    }
  };
</script>

<style scoped>
  .home__top {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 10px 0 16px;
  }

  .home__logo {
    display: block;
    width: 100%;
    height: auto;
    border-radius: 12px;
  }

  .countdown {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
    width: 100%;
    max-width: 320px;
  }

  .countdown__part {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
  }

  .countdown__value {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 10px 4px;
    border-radius: 10px;
    background: var(--tb-surface);
    box-shadow: var(--tb-shadow);
    font-size: clamp(16px, 5vw, 22px);
    font-variant-numeric: tabular-nums;
  }

  .countdown__label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    color: var(--tb-ink-soft);
  }

  .when {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
  }

  .when__cell {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 12px 14px;
  }

  .when__cell + .when__cell {
    border-left: 1px solid var(--tb-line);
  }

  .when__label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--tb-ink-soft);
  }

  .when__value {
    font-size: clamp(14px, 4vw, 17px);
  }

  /* Extra breathing room every sixth ticket marks a full-sheet boundary. */
  .is-sheet-end {
    margin-bottom: 12px;
  }

  .home__actions {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: var(--tb-space);
  }

  .home__search {
    margin-top: var(--tb-space-lg);
  }

  .home__pagination {
    margin-top: var(--tb-space-lg);
  }

  .home__footer {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-top: var(--tb-space-lg);
    padding: 16px;
    border-radius: var(--tb-radius);
    background: var(--tb-accent);
    color: #3b2a10;
    text-align: center;
  }

  .home__footer small {
    font-size: 11px;
  }
</style>
